/**
 * Duplicate Control Dialog Component
 * Phase 4: UX improvement for control duplication
 * Phase 5: Refactored to use shared Modal and Button components
 * Allows users to choose where to place the duplicated control
 */

import React from 'react';
import { Modal } from './shared/Modal';
import { Button } from './shared/Button';
import '../styles/DuplicateControlDialog.css';

export type PlacementOption = 'after' | 'before' | 'end' | 'start';

export interface DuplicateControlDialogProps {
  /**
   * Whether dialog is open
   */
  isOpen: boolean;

  /**
   * Control label being duplicated
   */
  controlLabel: string;

  /**
   * Callback when user confirms placement
   */
  onConfirm: (placement: PlacementOption) => void;

  /**
   * Callback when user cancels
   */
  onCancel: () => void;
}

/**
 * Duplicate Control Dialog
 * Shows placement options for duplicated controls
 */
export const DuplicateControlDialog: React.FC<DuplicateControlDialogProps> = ({
  isOpen,
  controlLabel,
  onConfirm,
  onCancel,
}) => {
  const [selectedPlacement, setSelectedPlacement] = React.useState<PlacementOption>('after');
  const [isConfirming, setIsConfirming] = React.useState(false);

  // Reset selection and loading state when dialog opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedPlacement('after');
      setIsConfirming(false);
    }
  }, [isOpen]);

  // Handle confirm with loading state
  const handleConfirm = () => {
    setIsConfirming(true);
    // Small delay to show loading spinner
    setTimeout(() => {
      onConfirm(selectedPlacement);
    }, 100);
  };

  // Handle Enter key to confirm
  React.useEffect(() => {
    if (!isOpen || isConfirming) return;

    const handleEnter = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleConfirm();
      }
    };

    document.addEventListener('keydown', handleEnter);
    return () => document.removeEventListener('keydown', handleEnter);
  }, [isOpen, isConfirming]);

  const placementOptions: Array<{ value: PlacementOption; label: string; icon: string; description: string }> = [
    {
      value: 'after',
      label: 'After Current',
      icon: '⬇️',
      description: 'Place right after the selected control',
    },
    {
      value: 'before',
      label: 'Before Current',
      icon: '⬆️',
      description: 'Place right before the selected control',
    },
    {
      value: 'end',
      label: 'At End',
      icon: '⤵️',
      description: 'Place at the end of all controls',
    },
    {
      value: 'start',
      label: 'At Beginning',
      icon: '⤴️',
      description: 'Place at the beginning of all controls',
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      size="small"
      closeOnEscape={!isConfirming}
      closeOnBackdropClick={!isConfirming}
      showCloseButton={false}
      className="af-duplicate-dialog"
      contentClassName="af-duplicate-dialog__modal-content"
      ariaLabel={`Duplicate ${controlLabel}`}
      footer={
        <>
          <div className="af-duplicate-dialog__hint">
            <kbd>Enter</kbd> to confirm • <kbd>Esc</kbd> to cancel
          </div>
          <div className="af-duplicate-dialog__actions">
            <Button
              variant="secondary"
              onClick={onCancel}
              disabled={isConfirming}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={isConfirming}
              loading={isConfirming}
            >
              {isConfirming ? 'Duplicating...' : 'Duplicate Control'}
            </Button>
          </div>
        </>
      }
    >
      {/* Header */}
      <div className="af-duplicate-dialog__header">
        <h3 className="af-duplicate-dialog__title">
          Duplicate "{controlLabel}"
        </h3>
        <p className="af-duplicate-dialog__subtitle">
          Choose where to place the duplicated control
        </p>
      </div>

      {/* Placement Options */}
      <div className="af-duplicate-dialog__options">
        {placementOptions.map((option) => (
          <label
            key={option.value}
            className={`af-duplicate-dialog__option ${
              selectedPlacement === option.value ? 'af-duplicate-dialog__option--selected' : ''
            }`}
          >
            <input
              type="radio"
              name="placement"
              value={option.value}
              checked={selectedPlacement === option.value}
              onChange={(e) => setSelectedPlacement(e.target.value as PlacementOption)}
              className="af-duplicate-dialog__radio"
            />
            <div className="af-duplicate-dialog__option-content">
              <span className="af-duplicate-dialog__option-icon">{option.icon}</span>
              <div className="af-duplicate-dialog__option-text">
                <span className="af-duplicate-dialog__option-label">{option.label}</span>
                <span className="af-duplicate-dialog__option-description">{option.description}</span>
              </div>
            </div>
          </label>
        ))}
      </div>
    </Modal>
  );
};

export default DuplicateControlDialog;
